import { useState, useEffect } from "react";
import Modal from "../../components/common/Modal";
import CrudForm from "../../components/common/crud/CrudForm";
import vendorService from "../../services/vendorService";
import countryService from "../../services/countryService";
import alertService from "../../utils/alertService";
import { normalizeApiError } from "../../utils/apiError";
import { useMasterModals } from "../../hooks/useMasterModals";

const vendorFormSchema = [
  {
    name: "vendorName",
    label: "Vendor Name",
    type: "text",
    placeholder: "ABC Metals Pvt Ltd",
    validation: { required: true, minLength: 3, maxLength: 100 },
  },
  {
    name: "vendorShortName",
    label: "Short Name",
    type: "text",
    placeholder: "ABC",
    validation: { required: true, minLength: 3, maxLength: 100 },
  },
  {
    name: "address",
    label: "Address",
    type: "textarea",
    placeholder: "Mumbai",
    validation: { required: true },
  },
  {
    name: "countryId",
    label: "Country",
    type: "cascading dropdown",
    placeholder: "Search country",
    parentField: null,
    optionMap: {},
    validation: { required: true },
  },
  {
    name: "emailId",
    label: "Email",
    type: "text",
    placeholder: "contact@abcmetals.com",
    validation: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      patternMessage: "Enter a valid email address",
    },
  },
  {
    name: "mobileNo",
    label: "Mobile No",
    type: "text",
    placeholder: "9876543210",
    validation: {
      required: true,
      pattern: /^\d{10}$/,
      patternMessage: "Mobile No must be 10 digits",
      maxLength: 10,
      restrictTo: "digits",
    },
  },
  {
    name: "isActive",
    label: "Is Active",
    type: "checkbox",
    defaultValue: true,
  },
];

function VendorCreateModal({ isOpen, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

  const loadCountries = async () => {
    try {
      const countries = await countryService.getActiveCountries();
      setCountryOptions(
        countries.map((c) => ({
          label: c.countryName || c.name,
          value: c.countryId || c.id,
        }))
      );
    } catch (err) {
      console.error("Failed to load countries", err);
    }
  };

  const { openModal, renderModals } = useMasterModals({
    country: loadCountries,
  });

  useEffect(() => {
    if (isOpen) {
      loadCountries();
    }
  }, [isOpen]);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      await vendorService.createVendor(values);
      alertService.success("Vendor created successfully.");
      onSuccess?.();
      onClose();
    } catch (err) {
      alertService.error(normalizeApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Vendor">
      <CrudForm
        mode="create"
        schema={vendorFormSchema.map((f) =>
          f.name === "countryId"
            ? {
                ...f,
                optionMap: { "": countryOptions },
                onAdd: () => openModal("country"),
                addLabel: "Add New Country",
              }
            : f
        )}
        initialValues={{ isActive: true }}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={onClose}
      />
      {renderModals()}
    </Modal>
  );
}

export default VendorCreateModal;
